import { create } from 'zustand'
import { HeyProfile } from '@/models/profile.ts'
import { produce } from 'immer'
import * as Sentry from "@sentry/react";

const AUTH_TOKEN_KEY = 'authenticationToken'
const GUEST_AUTH_TOKEN_KEY = 'authenticationTokenGuest'
const GUEST_TOKEN_KEY = 'guestModeEnabled'

interface UseAuthState {
  profile: HeyProfile | null
  authenticationToken: string | null
  isGuestMode: boolean
}

interface UseAuthActions {
  setProfile: (profile: HeyProfile | null) => void
  setAuthenticationToken: (token: string | null) => void
  setGuestMode: (enabled: boolean) => void
  logout: () => void
}

export const useProfile = create<UseAuthState & UseAuthActions>((set) => {
  const searchParams = new URLSearchParams(document.location.search)
  const tokenFromUri = searchParams.get('_ha') || null
  const isGuestModeEnabledFromStorage = localStorage.getItem(GUEST_TOKEN_KEY) == 'true'
  if (tokenFromUri) {
    localStorage.setItem(AUTH_TOKEN_KEY, tokenFromUri)
  }
  // When `_clha` is present, strip both the auth token (`_ha`) and `_clha` from
  // the URL after it has been consumed, without reloading the page. This runs
  // exactly once (at store creation / module init).
  if (searchParams.has('_clha')) {
    searchParams.delete('_ha')
    searchParams.delete('_clha')
    const newSearch = searchParams.toString()
    const newUrl = document.location.pathname + (newSearch ? `?${newSearch}` : '') + document.location.hash
    window.history.replaceState(window.history.state, '', newUrl)
  }
  const tokenFromStorage = isGuestModeEnabledFromStorage ? localStorage.getItem(GUEST_AUTH_TOKEN_KEY) : localStorage.getItem(AUTH_TOKEN_KEY)
  return {
    profile: null,
    authenticationToken: tokenFromUri || tokenFromStorage || null,
    setProfile(profile) {
      set(produce(state => {
        state.profile = profile
      }))
      if (profile) {
        Sentry.setUser({
          id: profile.username,
        })
      } else {
        Sentry.setUser(null)
      }
    },
    setAuthenticationToken(authenticationToken) {
      set(produce(state => {
        state.authenticationToken = authenticationToken
        const storageKey = isGuestModeEnabledFromStorage ? GUEST_AUTH_TOKEN_KEY : AUTH_TOKEN_KEY
        if (authenticationToken) {
          localStorage.setItem(storageKey, authenticationToken)
        } else {
          localStorage.removeItem(storageKey)
        }
      }))
    },
    logout()  {
      set(produce(state => {
        state.authenticationToken = null
        state.profile = null
        localStorage.removeItem(AUTH_TOKEN_KEY)
      }))
    },
    setGuestMode(enabled: boolean) {
      // set(produce(() => {
      //   state.isGuestMode = enabled
      if (enabled) {
        localStorage.setItem(GUEST_TOKEN_KEY, 'true')
      } else {
        localStorage.removeItem(GUEST_TOKEN_KEY)
        localStorage.removeItem(GUEST_AUTH_TOKEN_KEY)
      }
      // }))
    },
    isGuestMode: isGuestModeEnabledFromStorage,
  }
})
